import { formatStackTrace } from './ErrorUtils';
import { safeJSONStringify } from './utils';

export type ConsoleLogFunction = (...args: unknown[]) => void;
export type AppLogFunction = (
  level: number,
  message: string
) => void | Promise<void>;
export interface ReactErrorInfo {
  componentStack?: string | null;
}

// Python logging levels
const LEVEL_ERROR = 40;
const LEVEL_WARNING = 30;
const LEVEL_INFO = 20;
const LEVEL_DEBUG = 10;

/** Logs messages both to the console and, when provided, to the host app. */
export class Logger {
  constructor(private readonly appLog?: AppLogFunction) {}

  /** Send all arguments to console.debug, and the first arg to the host app. */
  debug(...args: unknown[]): void {
    this._log(console.debug, LEVEL_DEBUG, args);
  }

  /** Send all arguments to console.log, and the first arg to the host app. */
  info(...args: unknown[]): void {
    this._log(console.log, LEVEL_INFO, args);
  }

  /** Send all arguments to console.warn, and the first arg to the host app. */
  warning(...args: unknown[]): void {
    this._log(console.warn, LEVEL_WARNING, args);
  }

  /** Send all arguments to console.error, and the first arg to the host app. */
  error(...args: unknown[]): void {
    this._log(console.error, LEVEL_ERROR, args);
  }

  /** Log error from React Error Boundary */
  reactError = (error: unknown, errorInfo: ReactErrorInfo): void => {
    this.reportReactError(error, errorInfo);
  };

  private async reportReactError(
    error: unknown,
    errorInfo: ReactErrorInfo
  ): Promise<void> {
    console.error('Uncaught React error', error, errorInfo);
    const errorMessage = await this._stringify(error);
    const message = [
      'Uncaught React error',
      errorMessage,
      'React component stack:',
      errorInfo.componentStack,
    ].join('\n');
    try {
      await this.appLog?.(LEVEL_ERROR, message);
    } catch (appLoggingError) {
      console.error('App side logging failed', appLoggingError);
    }
  }

  private async _log(
    consoleFunc: ConsoleLogFunction,
    level: number,
    args: unknown[]
  ) {
    consoleFunc(...args);
    try {
      const message = await this._stringify(args[0]);
      await this.appLog?.(level, message);
    } catch (error) {
      console.error('App side logging failed', error);
    }
  }

  private _stringify(obj: unknown): string | Promise<string> {
    if (typeof obj === 'string') {
      return obj;
    }
    if (obj instanceof Error) {
      return formatStackTrace(obj).then(
        ({ message, stack }) => `${message ?? ''}\n${stack ?? ''}`
      );
    }
    return safeJSONStringify(obj);
  }
}
